import { IconAlertTriangle, IconBuildingStore, IconMapPin, IconUsers } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Loading } from "@/components/shared/loading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchAmazonProfiles, selectAmazonProfiles } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import { fetchAuthUser, hydrateAuthUser } from "@/lib/auth-api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

export const Route = createFileRoute("/_authed/amazon-select-profile")({
  component: AmazonSelectProfilePage,
  validateSearch: (search: Record<string, unknown>) => ({
    state: (search.state as string) || "",
    // Set when a re-authorization landed here because the Amazon login the
    // user completed can't see the profile they meant to re-authorize.
    reauth_miss: (search.reauth_miss as string) || "",
  }),
});

function AmazonSelectProfilePage() {
  const { state, reauth_miss } = Route.useSearch();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["amazon", "profiles", state],
    queryFn: () => fetchAmazonProfiles(state),
    enabled: !!state,
  });

  // Store exceptions rather than selections so every async-loaded profile is
  // selected by default without an effect that can overwrite user choices.
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());
  const profiles = data?.profiles ?? [];
  const selectedIds = new Set(
    profiles
      .filter((profile) => !deselectedIds.has(profile.profileId))
      .map((profile) => profile.profileId),
  );

  const selectMutation = useMutation({
    mutationFn: selectAmazonProfiles,
    onSuccess: async (data) => {
      hydrateAuthUser(await fetchAuthUser());
      const first = data.shops[0];
      if (first) {
        useAuthStore.getState().setCurrentShop(first.id);
      }
      queryClient.invalidateQueries();
      toast.success(t("profilePicker.connectSuccess", { count: data.shops.length }));
      navigate({ to: "/settings" });
    },
    onError: async (err) => {
      toast.error(await getApiErrorMessage(err));
    },
  });

  if (!state) {
    return <ErrorState message={t("profilePicker.missingState")} />;
  }

  if (isLoading) {
    return <Loading message={t("profilePicker.fetching")} />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : t("profilePicker.loadFailed")}
        onRetry={() => refetch()}
      />
    );
  }

  if (profiles.length === 0) {
    return (
      <EmptyState
        title={t("profilePicker.emptyTitle")}
        description={t("profilePicker.emptyDescription")}
      />
    );
  }

  function toggle(profileId: string) {
    setDeselectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-[18px] px-6 py-[22px]">
      <div>
        <h1 className="text-[24px] font-semibold leading-7 tracking-[-0.02em] text-ink">
          {t("profilePicker.title")}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">{t("profilePicker.description")}</p>
      </div>

      {reauth_miss && (
        <div className="flex items-start gap-2 rounded-[8px] border border-amber/20 bg-amber/5 px-3 py-2.5">
          <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-11" />
          <p className="text-xs leading-5 text-ink">{t("profilePicker.reauthMiss")}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
        {profiles.map((profile) => {
          const isSelected = selectedIds.has(profile.profileId);
          return (
            <button
              type="button"
              key={profile.profileId}
              aria-pressed={isSelected}
              aria-label={t("profilePicker.selectAria", { name: profile.name })}
              tabIndex={0}
              className={cn(
                "flex min-h-[62px] w-full cursor-pointer items-center border-b border-line px-4 text-left outline-none last:border-b-0 hover:bg-subtle/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                isSelected && "bg-subtle",
              )}
              onClick={() => toggle(profile.profileId)}
            >
              <div className="w-full">
                <div className="flex items-center gap-3">
                  {/* Visual indicator only — the CARD is the single toggle
                      target. A self-toggling checkbox plus the bubbling card
                      click flipped the state twice (= no visible change). */}
                  <span
                    className={cn(
                      "flex size-4 items-center justify-center rounded-[4px] border border-line",
                      isSelected &&
                        "border-ink bg-ink after:size-1.5 after:rotate-45 after:border-b after:border-r after:border-white",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <IconBuildingStore className="size-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-xs font-medium text-ink">{profile.name}</span>
                      {profile.alreadyConnected &&
                        (profile.connectedByOther ? (
                          <Badge variant="secondary" className="gap-1">
                            <IconUsers className="size-3" />
                            {t("profilePicker.connectedByOther")}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">{t("profilePicker.alreadyConnected")}</Badge>
                        ))}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <IconMapPin className="size-3" />
                        {profile.countryCode}
                      </span>
                      <span>{t("profilePicker.marketplaceId", { id: profile.marketplaceId })}</span>
                      <span className="font-mono">
                        {t("profilePicker.profileId", { id: profile.profileId })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Button
        className="ml-auto h-9 rounded-[8px] px-4"
        disabled={selectedIds.size === 0 || selectMutation.isPending}
        onClick={() =>
          selectMutation.mutate({
            profileIds: profiles
              .filter((profile) => selectedIds.has(profile.profileId))
              .map((profile) => profile.profileId),
            state,
          })
        }
      >
        {selectMutation.isPending
          ? t("profilePicker.connecting")
          : selectedIds.size > 1
            ? t("profilePicker.connectMany", { count: selectedIds.size })
            : t("profilePicker.connect")}
      </Button>
    </div>
  );
}
